import type { TodoState } from './todo-selectors';
import { STARTING, SUCCESS } from '../action/util/action-name-creators';

import type { Action } from './utils/state-manager';
import {
  updateResourceByParam,
  deleteResourceByParam,
  setStateDirtyByParam,
} from './utils/state-manager';
import { convertArrayToMap } from '../util/convert-array-to-map';

const InitialState: TodoState = {
  todosById: {},
  todosStateByOrgId: {},
  todosByJobId: {},
};

const markTodosDirty = (state: Record<string, any>, jobId: number) => {
  const todosByJobId = { ...state.todosByJobId };
  if (todosByJobId[jobId]) {
    todosByJobId[jobId].dirty = true;
  }

  return { ...state, todosByJobId };
};

const markAllTodosDirty = (state: Record<string, any>) => {
  const todosByJobId = { ...state.todosByJobId };
  Object.keys(todosByJobId).forEach((k) => {
    todosByJobId[k].dirty = true;
  });

  return { ...state, todosByJobId };
};

export default (state = InitialState, action: Action) => {
  switch (action.type) {
    case STARTING('LOAD_ORG_TODOS'): {
      const { orgId } = action.payload.args;
      return updateResourceByParam(state, 'todos', 'orgId', orgId, true);
    }
    case SUCCESS('LOAD_ORG_TODOS'): {
      const { args, data } = action.payload;
      const todosMap = convertArrayToMap(data, (t: Todo) => t.todoId.toString());
      return updateResourceByParam(state, 'todos', 'orgId', args.orgId, false, todosMap);
    }
    case 'LOAD_TODOS_STARTING': {
      const todosByJobId = { ...state.todosByJobId };
      todosByJobId[action.payload.jobId] = { ...todosByJobId[action.payload.jobId], loading: true };
      return { ...state, todosByJobId };
    }
    case 'LOAD_TODOS_SUCCESS': {
      const todosByJobId = { ...state.todosByJobId };
      todosByJobId[action.payload.jobId] = {
        todos: action.payload.todos,
        dirty: false,
        loading: false,
      };
      return { ...state, todosByJobId };
    }
    case 'LOAD_TODOS_FAILURE': {
      const todosByJobId = { ...state.todosByJobId };
      todosByJobId[action.payload.jobId] = {
        ...todosByJobId[action.payload.jobId],
        todos: [],
        dirty: false,
        loading: false,
        error: true,
        errorMessage: 'Error getting todos',
      };
      return { ...state, todosByJobId };
    }
    case SUCCESS('APPLICATION_STATUS'): {
      return markTodosDirty(state, action.payload.args.jobId);
    }
    case SUCCESS('SET_INTERVIEW'): {
      return markTodosDirty(state, action.payload.args.jobId);
    }
    /*
    DP - 9/17/2019
    This is currently used for forcing an applicant to be an HQC,
    which is admin-only. Since it's a bit challenging to get the jobId
    in here for this, I'm forcing all todos to refresh instead. Reconsider
    this approach if we broaden the use of UPDATE_JOB_APPLICANT and
    especially if we make it customer-facing.
     */
    case SUCCESS('UPDATE_JOB_APPLICANT'): {
      return markAllTodosDirty(state);
    }
    /*
    DP - 3/3/2019
    Since messages are at the contact-level, we don't really have
    information about the specific job. I went back and forth about
    this for a long time and decided that given the typical use
    of our software, it isn't unreasonable to simply refresh all
    to-do lists if a text was sent in the context of an applicant.
    Slightly inefficient in certain cases, but I think it's better
    than major hacks in the code right now.
     */
    case SUCCESS('SEND_TEXT_MESSAGE'): {
      const { context } = action.payload;
      /*
      If this message was sent in a context other than an applicant,
      don't refresh to-dos
       */
      if (!context.jobApplicantId) {
        return state;
      }
      return markAllTodosDirty(state);
    }
    case SUCCESS('DISMISS_TEXT_MESSAGE'): {
      const { context } = action.payload.args;
      /*
      If this message was dismissed in a context other than an applicant,
      don't refresh to-dos
       */
      if (!context.jobApplicantId) {
        return state;
      }
      return markAllTodosDirty(state);
    }
    case 'IGNORE_APPLICATION_SUCCESS': {
      return markTodosDirty(state, action.payload.jobId);
    }
    case SUCCESS('CREATE_DEMO_APPLICANT'): {
      return markTodosDirty(state, action.payload.data.job_id);
    }
    case SUCCESS('APPLICATION_UPDATED'): {
      return markAllTodosDirty(state);
    }
    case SUCCESS('MOVE_JOB_APPLICANT'): {
      return markAllTodosDirty(state);
    }
    case SUCCESS('UPDATE_TODO'): {
      const { jobId, orgId, completionTime, todoId } = action.payload.data;
      let newState = markTodosDirty(state, jobId);
      if (completionTime) {
        newState = deleteResourceByParam(newState, 'todos', 'orgId', orgId, todoId);
      }
      newState = setStateDirtyByParam(newState, 'todos', 'orgId', orgId);

      return newState;
    }
    default:
      return state;
  }
};
